import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /**
   * Per-line debouncers for quantity updates — one shared debouncer would
   * collapse rapid edits on different lines into only the last one, silently
   * dropping the earlier line's change.
   * @type {Map<number, (event: Event) => void>}
   */
  #quantityDebouncers = new Map();

  /** @param {Event} event */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;

    const line = event.detail.cartLine ?? 0;
    let debounced = this.#quantityDebouncers.get(line);
    if (!debounced) {
      debounced = debounce(
        /** @param {Event} debouncedEvent */
        (debouncedEvent) => {
          if (debouncedEvent instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(debouncedEvent);
        },
        300
      );
      this.#quantityDebouncers.set(line, debounced);
    }
    debounced(event);
  };

  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * Serializes cart mutations so overlapping taps can't race each other —
   * blocking input during a round-trip (the old `cart-items-disabled`
   * approach) silently swallowed taps on slow connections.
   */
  #mutationQueue = Promise.resolve();

  /** Mutations dispatched but not yet settled. */
  #pendingMutations = 0;

  /** Line item keys with an in-flight removal, to ignore repeat taps. */
  #removingKeys = new Set();

  /**
   * Cart items from the newest `/cart/change` response of the current burst.
   * Line item keys are regenerated whenever an automatic discount recalculates
   * (the hash covers pricing), so keys captured from the DOM go stale after
   * the first mutation — queued mutations re-resolve against this snapshot.
   * @type {Array<{key: string}> | null}
   */
  #latestCartItems = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * A page restored from the back/forward cache shows the cart as it was
   * minutes ago — lines added/removed on other pages (or a cart emptied at
   * checkout) are missing, and an optimistic empty state may be baked into the
   * snapshot. Re-render from the server so both cart surfaces (drawer and
   * /cart page) wake up truthful.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    // A mutation burst survived the restore — its final response morphs to
    // newer state than a render started now would.
    if (this.#pendingMutations > 0) return;
    resetShimmer(this);
    sectionRenderer
      .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
      .then((sectionHTML) => this.#rebuildAcceleratedCheckout(sectionHTML))
      .catch(() => {});
  };

  /**
   * The morph deliberately never touches `<shopify-accelerated-checkout-cart>`
   * (see morph.js) so the wallet buttons aren't re-initialized on every cart
   * mutation — but that also means a back/forward-cache restore is stuck with
   * the pre-freeze element. Its hydrated internals (closed shadow DOM, wallet
   * iframes) don't reliably survive the freeze on iOS WebKit, leaving a blank
   * strip where Shop Pay / PayPal / Apple Pay should render. Swap it for the
   * pristine server markup out of the just-fetched section response: the
   * custom element re-upgrades on insertion and hydrates fresh buttons.
   * @param {string} sectionHTML - The section response the restore render used.
   */
  #rebuildAcceleratedCheckout(sectionHTML) {
    const current = this.querySelector('shopify-accelerated-checkout-cart');
    if (!current) return;
    const fresh = new DOMParser()
      .parseFromString(sectionHTML, 'text/html')
      .querySelector('shopify-accelerated-checkout-cart');
    if (fresh) current.replaceWith(fresh);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    const lineItemRow = this.#rowFromEvent(event) ?? this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      key: lineItemRow?.dataset.key,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Resolves the cart item row containing the event target. The row's
   * `data-key` is the only identifier that survives concurrent mutations —
   * baked 1-based line indices go stale as soon as another line is removed.
   * @param {Event} [event]
   * @returns {HTMLElement | null}
   */
  #rowFromEvent(event) {
    if (!(event?.target instanceof Element)) return null;
    return event.target.closest('tr[data-key]');
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index (1-based; may be stale mid-burst).
   * @param {Event} [event] - The originating event, used to resolve the exact row.
   */
  onLineItemRemove(line, event) {
    const cartItemRowToRemove = this.#rowFromEvent(event) ?? this.refs.cartItemRows[line - 1];
    const key = cartItemRowToRemove?.dataset.key;

    // Ignore repeat taps on a row whose removal is already in flight.
    if (cartItemRowToRemove?.classList.contains('removing')) return;
    if (key) {
      if (this.#removingKeys.has(key)) return;
      this.#removingKeys.add(key);
    }

    this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If this removal clears the last row, optimistically trigger the cart empty
    // state. Rows already animating out belong to earlier removals — don't count them.
    const activeRows = this.refs.cartItemRows.filter((row) => !row.classList.contains('removing'));
    const isEmptyCart = rowsToRemove.length == activeRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        // The scarcity pill sits in the drawer header, a sibling of both the
        // heading and this component — the item swap alone leaves it painted
        // over the empty state. Drop it here so it clears with the last row.
        document.querySelector('.cart-drawer__stock')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity. Dispatches the cart event immediately, then queues
   * the actual request — mutations run one at a time, in tap order, so taps
   * during an in-flight update are queued instead of silently swallowed.
   * @param {Object} config - The config.
   * @param {number} config.line - The line (1-based index fallback).
   * @param {string} [config.key] - The line item key (index-shift-proof, preferred).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const { key, quantity } = config;

    this.refs.cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: key ?? '', quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    // A fresh burst starts from DOM state — drop the previous burst's snapshot.
    if (this.#pendingMutations === 0) this.#latestCartItems = null;
    this.#pendingMutations++;
    this.#mutationQueue = this.#mutationQueue
      .then(() => this.#performCartChange(config, deferredUpdatePromise))
      .catch(() => {});
  }

  /**
   * Performs a single queued cart change request and settles its event promise.
   * The request body is built at send time: the line item key is re-resolved
   * against the newest response, and only the last pending mutation morphs the
   * section — intermediate responses are already-stale snapshots that would
   * re-render rows mid-removal.
   * @param {Object} config - The config passed to `updateQuantity`.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredUpdatePromise
   */
  async #performCartChange(config, deferredUpdatePromise) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const key = this.#freshKey(config.key);

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(key ? { id: key } : { line: config.line }),
      quantity: config.quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsedResponseText = JSON.parse(await response.text());
      const isLastPending = this.#pendingMutations === 1;

      // Error responses carry `errors` (legacy) or `description`/`message`
      // (e.g. HTTP 400/422) and never include the requested sections.
      const errorMessage =
        parsedResponseText.errors ||
        (parsedResponseText.sections
          ? null
          : parsedResponseText.description || parsedResponseText.message || 'Failed to update cart');

      if (errorMessage) {
        this.#handleCartError(config, errorMessage);
        deferredUpdatePromise.reject(new Error(errorMessage));
        return;
      }

      if (Array.isArray(parsedResponseText.items)) this.#latestCartItems = parsedResponseText.items;

      const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: parsedResponseText.sections,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      if (isLastPending) {
        resetShimmer(this);

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      }
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      // The UI may hold optimistic state the server never saw (e.g. a row
      // already animated out) — re-render to server truth.
      this.#resyncSection();
    } finally {
      this.#pendingMutations--;
      if (config.key) this.#removingKeys.delete(config.key);
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Maps a possibly-stale line item key to its current server-side value.
   * A discount recalculation regenerates keys but keeps the variant id prefix,
   * so a line is re-identified by that prefix within the newest snapshot.
   * @param {string} [key] - The key captured when the mutation was requested.
   * @returns {string | undefined} The freshest known key for that line.
   */
  #freshKey(key) {
    if (!key || !this.#latestCartItems) return key;
    if (this.#latestCartItems.some((item) => item.key === key)) return key;

    const variantPrefix = `${key.split(':')[0]}:`;
    return this.#latestCartItems.find((item) => item.key.startsWith(variantPrefix))?.key ?? key;
  }

  /**
   * Re-renders the section from the server, discarding optimistic UI state.
   * No-op while more mutations are queued — the burst's final response morphs
   * the section to server truth anyway.
   */
  #resyncSection() {
    if (this.#pendingMutations > 1) return;
    resetShimmer(this);
    sectionRenderer
      .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
      .catch(() => {});
  }

  /**
   * Handles the cart error.
   * @param {Object} config - The config passed to `updateQuantity`.
   * @param {string} errorMessage - The error message.
   */
  #handleCartError = (config, errorMessage) => {
    const { line, action } = config;
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (
      action !== 'clear' &&
      quantityInput &&
      cartItemError instanceof HTMLElement &&
      cartItemErrorContainer instanceof HTMLElement
    ) {
      resetShimmer(this);
      quantityInput.value = quantityInput.defaultValue;
      cartItemError.textContent = errorMessage;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      // A failed removal already animated its row out (or the row's refs are
      // stale) — re-render to server truth instead of showing inline errors.
      this.#resyncSection();
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    // A mutation burst is still in flight — its final response will morph the
    // section with newer state than this event carries; morphing here would
    // resurrect rows mid-removal.
    if (this.#pendingMutations > 0) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
